import { useState } from "react";
import type { BoletoDoPainel } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useListaDeBoletos, type FiltrosDeBoletos } from "@/api/consultas";
import { Dica } from "@/componentes/Dica";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { Tabela, type Coluna } from "@/componentes/Tabela";
import { PilulaDeSituacao } from "@/componentes/PilulaDeSituacao";
import { CarregarMais, contagemDaLista } from "@/telas/boletos/Boletos";
import { DetalheDoBoleto } from "@/telas/boletos/DetalheDoBoleto";
import { dia, dinheiro, documento, faz } from "@/formato";

/*
 * Indeterminados — doc 06 §4.3, "lista com destaque máximo".
 *
 * O conjunto vem do servidor, pela mesma definição da contagem da visão geral (a coluna
 * gerada `boleto.indeterminado`), e nada é filtrado aqui.
 *
 * O painel abre em cima da aba, sem navegar para Boletos (design D7), e fica fora do ramo
 * do erro: a releitura que falha não pode fechá-lo com uma resolução pela metade.
 */
const SO_INDETERMINADOS: FiltrosDeBoletos = { indeterminado: "true" };

export function Indeterminados() {
  const { cliente, estado } = useSessao();
  const [aberto, setAberto] = useState<BoletoDoPainel | null>(null);

  const lista = useListaDeBoletos(cliente, SO_INDETERMINADOS, estado.fase === "dentro");

  return (
    <div className="flex flex-col gap-3">
      {lista.itens.length > 0 && lista.erro === null && (
        <p className="flex items-center gap-1">
          <span className="rotulo">{contagemDaLista(lista)}</span>
          <Dica texto="96 h sem confirmação do provider. A resolução manual exige justificativa." />
        </p>
      )}

      {lista.erro !== null ? (
        <FaixaDeErro erro={lista.erro} />
      ) : (
        <>
          <Tabela
            rotulo="Indeterminados"
            colunas={COLUNAS}
            itens={lista.itens}
            carregando={lista.carregando}
            chaveDe={(boleto) => boleto.id}
            selecionado={aberto?.id}
            aoAbrir={setAberto}
            vazio="Nenhum indeterminado."
          />
          <CarregarMais lista={lista} />
        </>
      )}

      {aberto !== null && (
        <DetalheDoBoleto
          key={aberto.id}
          id={aberto.id}
          boletoDaLista={aberto}
          aoFechar={() => setAberto(null)}
        />
      )}
    </div>
  );
}

const COLUNAS: readonly Coluna<BoletoDoPainel>[] = [
  {
    chave: "situacao",
    rotulo: "Situação",
    largura: "132px",
    render: (boleto) => (
      <PilulaDeSituacao situacao={boleto.situacao} indeterminado={boleto.indeterminado} />
    ),
  },
  {
    chave: "referencia",
    rotulo: "Referência",
    largura: "minmax(120px, 1fr)",
    render: (boleto) => (
      <span className="dados truncate" title={boleto.id}>
        {boleto.referencia_externa ?? "—"}
      </span>
    ),
  },
  {
    chave: "sacado",
    rotulo: "Sacado",
    largura: "minmax(150px, 1.4fr)",
    render: (boleto) => (
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate">{boleto.sacado_nome || "—"}</span>
        <span className="dados truncate text-[11px] text-texto-3">
          {documento(boleto.sacado_documento)}
        </span>
      </div>
    ),
  },
  {
    chave: "valor",
    rotulo: "Valor",
    largura: "120px",
    direita: true,
    render: (boleto) => <span className="tabular">{dinheiro(boleto.valor_centavos)}</span>,
  },
  {
    chave: "vencimento",
    rotulo: "Vencimento",
    largura: "104px",
    // Em 1024 sai antes de "Parado há", que é quem ordena a urgência.
    descarte: 1,
    direita: true,
    render: (boleto) => <span className="tabular">{dia(boleto.vencimento)}</span>,
  },
  {
    chave: "parado",
    rotulo: "Parado há",
    largura: "96px",
    direita: true,
    // É a coluna que ordena a urgência na cabeça do operador: um indeterminado
    // de seis dias é mais grave que um de quatro.
    render: (boleto) => (
      <span className="tabular text-texto-2" title={boleto.atualizado_em}>
        {faz(boleto.atualizado_em)}
      </span>
    ),
  },
];
